import { queryKeys } from '@query/keys'
import { PaginatedQueryResult, usePaginatedQuery } from '@query/usePaginatedQuery'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { SortOrder, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'
import { RebateTransactionSortKeys } from '@model/RebateTransactionDto'
import { expandTransactionStates } from '@model/TransactionStatus'

interface IBRebateTransaction {
  query: PaginatedQueryResult<import('@model/RebateTransactionDto').RebateTransactionDto>
  sortOptions: NameDto<RebateTransactionSortKeys | null>[]
  search?: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  clearSearch: (callback: () => void) => void
}

export const useIBRebateTransactions = (): IBRebateTransaction => {
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const [search, setSearch] = useState<FilterQueryProps>()

  const sortOptions: NameDto<RebateTransactionSortKeys | null>[] = [
    { id: 'transactionDate', name: t('Date') },
    { id: 'tradingAccountName', name: t('Trading Account.Trading Account') },
    { id: 'accountType', name: t('Account Type') },
    { id: 'accumulatedVolumeFxm', name: t('IB.RebatesPage.ClientsPage.Vol. FXM') },
    { id: 'accumulatedVolumeOther', name: t('IB.RebatesPage.ClientsPage.Vol. Other') },
    { id: 'accumulatedCommission', name: t('Commission') },
    { id: 'rebateAmount', name: t('IB.RebatesPage.Rebates') },
    { id: 'state', name: t('Status') },
  ]

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  const defaultSort = 'transactionDate'
  const defaultSortOrder: SortOrder = 'DESC'

  const ibId = account?.clientIntroducingBroker?.id

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.rebates.transactions(ibId, { search, locale } as any),
    queryFn: (pageQuery) =>
      apiClient.getIBRebateTransaction(ibId ?? '', {
        ...pageQuery,
        search: expandTransactionStates(search),
        caller: 'ca',
        languageId: locale,
        sort: pageQuery?.sort ?? defaultSort,
        sortOrder: pageQuery?.sortOrder ?? defaultSortOrder,
        dismissNotifications: true,
      }),
  })

  return {
    query,
    sortOptions,
    search,
    setSearch,
    clearSearch,
  }
}
